import { type TripSearchForm } from '@search/trip/TripSearchForm';
import BusModule from '@itinerary/trip/bus/BusModule';
import FlightModule from '@itinerary/trip/flight/FlightModule';
import HotelModule from '@itinerary/hotel/HotelModule';
import TrainModule from '@itinerary/trip/train/TrainModule';
import {
    type Filter,
    type ListErrorMV,
    LoadingStatus,
} from '@itinerary/ItineraryModuleTypes';
import type ItineraryModule from '@itinerary/ItineraryModule';
import type TripModule from '@itinerary/trip/TripModule';
import type Trip from '@itinerary/trip/Trip';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { type ItineraryParams, ReserveType } from '@utils/itinerary';
import type { BreadcrumbItem } from '@components/base/mr-breadcrumb';
import { breadcrumbPaths } from '@store/layout';

const storeKey = 'itinerary';
export const useItineraryStore = defineStore(storeKey, () => {
    //#region modules
    const flightModule = reactive(
        new FlightModule()
    ) as unknown as FlightModule;
    const hotelModule = reactive(new HotelModule()) as unknown as HotelModule;
    const trainModule = reactive(new TrainModule()) as unknown as TrainModule;
    const busModule = reactive(new BusModule()) as unknown as BusModule;
    const taxiModule = reactive(
        new BusModule(
            'رزرو آنلاین و تهیه بلیط تاکسی و سواری دربستی بین شهری از (مبدا) به (مقصد)',
            ReserveType.Taxi
        )
    ) as unknown as BusModule;
    //#endregion

    const reserveType = computed(() => {
        const searchFormStore = useSearchFormStore();
        return searchFormStore.reserveType;
    });

    /**
     * The package set that is currently active
     */
    const currentSetIndex = ref(0);

    //#region computed
    const packageSetIndex = computed(() => currentSetIndex.value);

    const expectedPackagesToBeSelected = computed(
        () => currentItineraryModule().itineraryPackageSets.length
    );

    const targetPersianName = computed(() => {
        const hotelForm = useHotelForm();
        if (reserveType.value === ReserveType.Hotel) {
            return hotelForm.userDestination?.title ?? '';
        }
        return currentTripModule().destinationName;
    });

    const viewBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const currentModule = useCurrentItineraryModule<TripModule>();
        const contentStore = useContentStore();
        const cityName = currentModule.value.destinationName;
        const cityParam = currentSearchForm.value.destination?.code;
        const originParam = currentModule.value.originCurrentCode;
        const reserveType = currentSearchForm.value.reserveInfo.type;

        const isInternational =
            reserveType === ReserveType.Flight &&
            currentSearchForm.value.isForeign;

        const result: BreadcrumbItem[] = [{ link: '/', title: 'مِستربلیط' }];
        const currentPath =
            breadcrumbPaths[
                `${isInternational ? 'international-' : ''}${reserveType}`
            ];
        result.push(currentPath[0]);
        if (isInternational && currentPath[1]) {
            result.push(currentPath[1]);
        }
        const cityIndex = isInternational ? 2 : 1;
        if (cityName && contentStore.hasLandingContents) {
            result.push({
                link: currentPath[cityIndex].link.replace('$city', cityParam!),
                title: currentPath[cityIndex].title.replace(
                    '$cityName',
                    cityName
                ),
            });
        }
        const routeIndex = isInternational ? 3 : 2;
        if (originParam) {
            result.push({
                link: currentPath[routeIndex].link
                    .replace('$city', cityParam!)
                    .replace('$ORG', originParam),
                title: currentPath[routeIndex].title
                    .replace('$cityName', cityName)
                    .replace('$ORGName', currentModule.value.originName),
            });
        }
        return result.filter(item => item !== null);
    });

    /**
     * get title of current itinerary module
     */
    const reserveName = computed(
        () => useCurrentSearchForm().value.reserveInfo.title
    );

    const loadingStatus = computed(
        () => currentItineraryModule().currentLoadingStatus
    );

    /**
     * all package loaded form current itinerary module
     */
    const allPackagesLoaded = computed(
        () => currentItineraryModule().currentAllPackagesLoaded
    );

    const filtersLoading = computed(
        () => currentItineraryModule().filtersLoading
    );

    /**
     * Indicates whether the itinerary packages are being loaded, or not
     */
    const loading = computed(
        () => loadingStatus.value === LoadingStatus.Loading
    );

    /**
     * Indicates whether the itinerary package loaded success or not
     */
    const isLoadedSuccess = computed(
        () => loadingStatus.value === LoadingStatus.Success
    );

    /**
     * Returns the list of trip packages, of the currently active reserveType
     */
    const list = computed(() =>
        !reserveType.value ? [] : currentItineraryModule().list
    );

    /**
     * Returns the count of trip packages, before applying filters
     */
    const unfilteredCount = computed(() =>
        !reserveType.value ? 0 : currentItineraryModule().unfilteredCount
    );

    const packagesLength = computed<number>(
        () =>
            currentItineraryModule().sortedReservableList.length +
            currentItineraryModule().sortedNonReservableList.length
    );

    const lowestPricePromoted = computed(() =>
        currentItineraryModule()
            .list.filter(({ defaultPrice }) => defaultPrice?.hasPriority)
            .reduce<ItineraryPackage | null>(
                (prev, next) =>
                    prev === null || prev.singlePayable > next.singlePayable
                        ? next
                        : prev,
                null
            )
    );

    const reservableList = computed(() =>
        currentItineraryModule().sortedReservableList.filter(
            ({ uniqueId }) => uniqueId !== lowestPricePromoted.value?.uniqueId
        )
    );

    const resultStatus = computed(() => {
        switch (loadingStatus.value) {
            case LoadingStatus.Error:
                return 'error';
            case LoadingStatus.Offline:
                return 'offline';
            case LoadingStatus.Success:
                switch (true) {
                    case !reservableList.value.length &&
                        !nonReservableList.value.length:
                        return 'noResults';
                    case !!reservableList.value.length &&
                        !!nonReservableList.value.length:
                        return 'haveReservableAndNoneReservableList';
                    case !!reservableList.value.length:
                        return 'haveReservableList';
                    case !!nonReservableList.value.length:
                    default:
                        return 'haveNoneReservableList';
                }
            default:
                return null;
        }
    });

    const listTips = computed(
        () => currentItineraryModule().currentPackageSet?.listTips || []
    );

    const nonReservableList = computed(() => {
        if (hasAutoReserve.value) {
            return currentItineraryModule().sortedNonReservableList.filter(
                pck => !pck.autoReserveEnabled
            );
        } else {
            return currentItineraryModule().sortedNonReservableList;
        }
    });

    const emptyPackage = computed<ItineraryPackage | null>(() => {
        try {
            return currentItineraryModule().emptyPackage;
        } catch {
            return null;
        }
    });

    const hasAutoReserve = computed<boolean>(
        () => currentItineraryModule().hasAutoReserve
    );

    /**
     * Returns the list of promoted trip packages, to be shown in a special section
     */
    const promotedList = computed(() =>
        !reserveType.value ? [] : currentItineraryModule().promotedList
    );

    const isSelectingReturn = computed<boolean>(
        () => currentSetIndex.value === 1
    );

    const showChangeDateSuggestion = computed(
        () => currentItineraryModule().showChangeDateSuggestion
    );

    /**
     * Returns the minimum payable price for single adult passenger of trip packages of current view page
     */
    const minPrice = computed(() => {
        if (!list.value || list.value.length === 0) {
            return null;
        }
        return Math.min(
            ...list.value.map(p => p.singlePayable).filter(p => p > 0)
        );
    });

    /**
     * Returns the current applied filters of current view page
     */
    const filters = computed<Filter[]>(() =>
        currentItineraryModule().filters.map(filter => ({
            ...filter,
            items:
                filter.items?.filter(
                    (pkg, i) =>
                        filter.items?.findIndex(p => p.id === pkg.id) === i
                ) ?? null,
        }))
    );

    /**
     * Returns the selected value current applied filters of current view page
     */
    const filterValues = computed(
        () => currentItineraryModule().currentPackageSet?.filters
    );

    /**
     * Indicates whether all the trip packages in current page is full or not
     */
    const areAllTicketsFull = computed(
        () =>
            !currentItineraryModule().currentItineraryPackages?.some(
                item => item.reservable
            )
    );

    /**
     * Indicates whether the search for trips has any results or not
     */
    const noResults = computed(
        () =>
            loadingStatus.value === LoadingStatus.Success &&
            unfilteredCount.value === 0
    );

    const listErrorReason = computed<ListErrorMV>(
        () =>
            currentItineraryModule().listErrorReason ||
            currentItineraryModule().unknownListError
    );

    /**
     * Indicates whether applying filters left any trip package or not
     */
    const noFilteredResults = computed(
        () =>
            loadingStatus.value === LoadingStatus.Success &&
            packagesLength.value === 0
    );
    //#endregion

    /**
     * Returns the trip module related to {reserveType}
     */
    const getItineraryModule = <T extends ItineraryModule = ItineraryModule>(
        type: ReserveType
    ): T => {
        switch (type || ReserveType.Flight) {
            case ReserveType.Flight:
                return flightModule as any;
            case ReserveType.Train:
                return trainModule as any;
            case ReserveType.Bus:
                return busModule as any;
            case ReserveType.Taxi:
                return taxiModule as any;
            case ReserveType.Hotel:
                return hotelModule as any;
            default:
                return flightModule as any;
        }
    };

    /**
     * Returns the itinerary module related to reserveType currently being viewed
     */
    const currentItineraryModule = <
        T extends ItineraryModule = ItineraryModule,
    >(): T => getItineraryModule<T>(reserveType.value);

    /**
     * Returns the trip module related to reserveType currently being viewed
     */
    const currentTripModule = (): TripModule<Trip, TripPriceClass> =>
        getItineraryModule<TripModule>(reserveType.value);

    const setPackageSetIndex = (index: number) => {
        currentSetIndex.value = index;
    };

    /**
     * just call it in client side
     */
    const initItineraryModule = (params: ItineraryParams) => {
        const currentModule = currentItineraryModule();
        currentModule.init(params);
    };

    /**
     * force load available by reset last load times
     */
    const forceLoadAvailable = (resetOnly = false) => {
        currentItineraryModule().resetLoaders();
        if (!resetOnly) {
            loadAvailable();
        }
    };

    /**
     * Loads available trips for selected origin and destination and reserveType and date from server
     */
    const loadAvailable = (params?: { index?: number }) => {
        if (typeof params === 'undefined') {
            params = {
                index: packageSetIndex.value,
            };
        }

        if (typeof params.index === 'undefined') {
            params.index = packageSetIndex.value;
        }

        setPackageSetIndex(params.index || 0);
        if (currentItineraryModule().isSetLoaded(params.index || 0)) {
            return;
        }

        return currentItineraryModule().loadAvailable({
            index: params.index || 0,
        });
    };

    const needsReloadPackageSet = (index?: number) => {
        const itineraryPackageSet =
            currentItineraryModule().itineraryPackageSets[
                index ?? packageSetIndex.value
            ];
        if (itineraryPackageSet) {
            return itineraryPackageSet.needsReload;
        }
        return false;
    };

    /**
     * Clear all package sets in current itinerary module
     */
    const clearPackageSets = () => {
        currentSetIndex.value = 0;
        usePackageSelectStore().clearSelectedPackages();
        useFormStore().itineraryPackages = [];
        EnumClass.values(ReserveType).forEach(reserveInfo => {
            getItineraryModule(reserveInfo).clear();
        });
    };

    /**
     * Changes the current departure date, by the specified offset
     */
    const navigate = () => {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const currentDepartureTime = currentSearchForm.value.departureDate;
        if (!currentDepartureTime) {
            return;
        }
        currentSearchForm.value.departureDate = dayConfig
            .gregory(currentDepartureTime)
            .toDate();
        loadAvailable();
    };

    return {
        flightModule,
        hotelModule,
        trainModule,
        busModule,
        taxiModule,
        currentSetIndex,
        packageSetIndex,
        expectedPackagesToBeSelected,
        targetPersianName,
        reserveName,
        allPackagesLoaded,
        loading,
        isLoadedSuccess,
        list,
        listTips,
        unfilteredCount,
        packagesLength,
        reservableList,
        lowestPricePromoted,
        nonReservableList,
        emptyPackage,
        hasAutoReserve,
        promotedList,
        isSelectingReturn,
        showChangeDateSuggestion,
        minPrice,
        filters,
        filterValues,
        areAllTicketsFull,
        noResults,
        resultStatus,
        listErrorReason,
        noFilteredResults,
        getItineraryModule,
        setPackageSetIndex,
        initItineraryModule,
        forceLoadAvailable,
        loadAvailable,
        needsReloadPackageSet,
        clearPackageSets,
        navigate,
        filtersLoading,
        viewBreadcrumbItems,
    };
});
